/**
 * docs/05-ADMIN-DASHBOARD.md §4.2 — "Pasting from Word strips inline styles,
 * `<font>`, `class`, and `style` attributes."
 *
 * This is the one rule in §4.2 that is about *quality* rather than safety. A
 * paste out of Word carries `mso-` styles, `<o:p>` tags, fourteen nested spans
 * and a `class="MsoNormal"` on every paragraph — none of it dangerous, all of
 * it enough to make the article render in Calibri on a site that uses Manrope,
 * with line heights nobody chose.
 *
 * `HtmlSanitizer` on the server drops these too, so nothing here is the last
 * line of defence. Doing it at paste time means the editor *sees* clean
 * content, rather than watching their formatting disappear after they save.
 */

/** Attributes Word leaves behind that carry presentation. */
const STRIPPED_ATTRIBUTES = ['style', 'class', 'lang', 'align', 'bgcolor', 'width', 'height'];

/**
 * Elements that are pure presentation and have no allowlisted equivalent.
 * Their *children* are kept — unwrapping rather than deleting, because the text
 * inside a `<font>` is the thing the editor meant to paste.
 */
const UNWRAPPED = ['font', 'span', 'o:p', 'w:sdt', 'style', 'meta', 'link'];

/** The subset of those whose contents go with them. */
const DISCARDED = ['style', 'meta', 'link'];

/**
 * Cleans a pasted HTML fragment.
 *
 * Runs on a detached document, so nothing here can execute: `DOMParser` does
 * not run scripts, load images or apply styles, which is what makes it safe to
 * parse untrusted markup at all.
 */
export function cleanPastedHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  parsed.body.querySelectorAll('*').forEach((element) => {
    for (const attribute of STRIPPED_ATTRIBUTES) {
      element.removeAttribute(attribute);
    }

    // Word's own namespaced attributes, which vary too much to list.
    for (const attribute of [...element.attributes]) {
      if (/^(mso-|v:|o:|w:|x:|data-mce-)/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    }
  });

  /*
   * Matched on `nodeName` rather than with `querySelector`.
   *
   * Word writes namespaced tags — `<o:p>`, `<w:sdt>` — and a colon in a CSS
   * selector is a pseudo-class, so `querySelector('o:p')` does not fail to
   * match, it *throws*. Walking the tree costs nothing on a paste and handles
   * both kinds of name.
   */
  let unwrapped = true;

  while (unwrapped) {
    unwrapped = false;

    for (const element of [...parsed.body.querySelectorAll('*')]) {
      if (!UNWRAPPED.includes(element.nodeName.toLowerCase())) {
        continue;
      }

      if (DISCARDED.includes(element.nodeName.toLowerCase())) {
        // These carry no text worth keeping — Word's `<style>` block holds the
        // `mso-` definitions the attributes above referred to.
        element.remove();
      } else {
        // Unwrap rather than delete: the text inside a `<font>` is the thing
        // the editor meant to paste.
        element.replaceWith(...element.childNodes);
      }

      // Unwrapping one `<span>` can expose another that was nested inside it.
      unwrapped = true;
    }
  }

  /*
   * Word writes an empty paragraph between every real one. Left in, an article
   * pasted from a Word document renders with double spacing throughout, and
   * the editor deletes them by hand one at a time.
   */
  parsed.body.querySelectorAll('p').forEach((paragraph) => {
    if (paragraph.textContent?.trim() === '' && paragraph.children.length === 0) {
      paragraph.remove();
    }
  });

  return parsed.body.innerHTML;
}

/** Whether a paste looks like it came out of Word or Google Docs. */
export function looksLikeOfficePaste(html: string): boolean {
  return /class="?Mso|mso-|urn:schemas-microsoft-com|docs-internal-guid/i.test(html);
}
